import { Controller } from "@hotwired/stimulus"
import * as echarts from "echarts/core"
import { BarChart } from "echarts/charts"
import { LineChart } from "echarts/charts"
import { GridComponent, TooltipComponent, LegendComponent, MarkAreaComponent, MarkLineComponent } from "echarts/components"
import { CanvasRenderer } from "echarts/renderers"

echarts.use([BarChart, LineChart, GridComponent, TooltipComponent, LegendComponent, MarkAreaComponent, MarkLineComponent, CanvasRenderer])

// Two panels sharing one category axis: the pass rate on its own zoomed scale above, the 100%
// composition stack below.
//
// One chart instance with two grids rather than two charts, because the alignment is the point. A
// reader drops a finger from a dip in the line to the mix underneath it, and two independent charts
// stop lining up the moment their axis labels differ in width.
//
// Every number arrives computed from Pipeline::RulePassRateChart — the axis range, the reference
// band, and each day's verdict. This controller decides how they are drawn and nothing about what
// they mean.
//
// Room reserved for the y-axis labels in BOTH panels, so their plot areas are identical widths and
// a bar lines up with the line point above it. Sized for the widest label either axis can produce.
const AXIS_GUTTER = 54

// How much of a series name the legend shows before trimming. The tooltip shows all of it.
const LEGEND_MAX_CHARS = 44

// Carries the stack panel's column shading. Named so it can be kept out of the legend.
const COLUMN_SHADING_SERIES = "__column_shading"

// The pass rate's own trace.
const PASS_LINE_COLOR = "#2C3131"

// The "this is normal" region behind the line, and the dashed median through it.
const BAND_COLOR = "#F0EBFB"
const MEDIAN_COLOR = "#9CA6A4"

// A column nothing was measured in. Deliberately not the weekend grey — most no-run days ARE
// weekends and are already shaded, so this has to be legible ON TOP of that to explain the gap on
// the weekday that is the real question.
const NO_RUN_COLOR = "#FEF3C7"

// A day beyond the baseline's ordinary spread, coloured as the pipeline page's calendar grid
// colours it, so a day flagged there is flagged the same way here.
const LEVEL_COLORS = {
  strong_below: "#d03b3b",
  below: "#fab219",
  above: "#3d8f3d",
  strong_above: "#226022",
}

// Connects to data-controller="rule-pass-rate-chart"
export default class extends Controller {
  static values = {
    categories: Array,
    // The stacked bars, bottom of the stack first. No line series here — the pass rate is panel 0.
    stackSeries: Array,
    // [{ value: Number|null, level: String }] — value in percentage points, null on a day with no
    // run. `level` is the shared baseline's verdict for that day.
    passPoints: Array,
    // { min, max } in percentage points, sized server-side to hold the data and the whole band.
    passAxis: Object,
    // { median, low, high, days } in percentage points, or absent when there is no baseline yet.
    baseline: Object,
    weekendBands: { type: Array, default: [] },
    weekendColor: { type: String, default: "#e5e7eb" },
    labelColors: { type: Array, default: [] },
  }

  connect() {
    this.chart = echarts.init(this.element)
    this.render()

    this.resizeObserver = new ResizeObserver(() => this.chart.resize())
    this.resizeObserver.observe(this.element)
  }

  disconnect() {
    this.resizeObserver?.disconnect()
    this.chart?.dispose()
  }

  render() {
    this.chart.setOption({
      legend: {
        bottom: 0,
        // Six rule bands at LEGEND_MAX_CHARS overflow the space grid[1].bottom reserves on a narrow
        // viewport, and an overflowing legend paints over the stack.
        type: "scroll",
        icon: "roundRect",
        itemWidth: 10,
        itemHeight: 10,
        textStyle: { color: "#4b5563", fontSize: 11 },
        // The pass series is named in the panel's own heading, so it stays out of the legend that
        // describes the stack.
        data: this.stackSeriesValue.map((series) => series.name),
        // Only the DISPLAY is trimmed: the name is the key ECharts matches series on, and the
        // tooltip shows it in full.
        formatter: (name) => (name.length > LEGEND_MAX_CHARS ? `${name.slice(0, LEGEND_MAX_CHARS - 1)}…` : name),
      },
      // Panel 0 is the pass rate, panel 1 the stack. The stack gets the larger share: it carries
      // five or six bands, where the line carries one.
      //
      // A FIXED left gutter on both, and deliberately NOT containLabel. containLabel sizes each
      // grid's plot area around its own axis labels, and these two panels have labels of different
      // widths — "27.56%" against "25%" — so each would start at a different x and every bar would
      // sit a few pixels off the line point above it. Alignment is the whole reason the two panels
      // share a chart, so the gutter is reserved for the widest label either axis can produce
      // ("100.00%") and both panels get the same plot width by construction.
      grid: [
        { left: AXIS_GUTTER, right: 12, top: 8, height: 74 },
        { left: AXIS_GUTTER, right: 12, top: 126, bottom: 56 },
      ],
      // Linked pointers, so hovering either panel marks the same day in both. Without this the two
      // panels read as unrelated pictures that happen to sit together.
      axisPointer: { link: [{ xAxisIndex: "all" }] },
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        backgroundColor: "#1f2937",
        borderWidth: 0,
        textStyle: { color: "#f9fafb", fontSize: 12 },
        valueFormatter: (value) => (value == null ? "—" : `${Number(value).toFixed(2)}%`),
      },
      xAxis: [
        // The top panel borrows the bottom panel's labels rather than repeating them: one axis of
        // dates for two panels that share it.
        //
        // boundaryGap is stated on both rather than left to the default. A category axis puts its
        // categories in bands and centres marks within them, which is what lines a line point up
        // over a bar — but the two panels only agree while they agree about this, and a default is
        // an agreement nobody wrote down.
        {
          type: "category",
          gridIndex: 0,
          data: this.categoriesValue,
          boundaryGap: true,
          axisLine: { lineStyle: { color: "#e5e7eb" } },
          axisTick: { show: false },
          axisLabel: { show: false },
        },
        {
          type: "category",
          gridIndex: 1,
          data: this.categoriesValue,
          boundaryGap: true,
          axisLine: { lineStyle: { color: "#e5e7eb" } },
          axisTick: { show: false },
          axisLabel: {
            fontSize: 11,
            hideOverlap: true,
            color: (_value, index) => this.labelColorsValue[index] || "#6b7280",
          },
        },
      ],
      yAxis: [
        {
          type: "value",
          gridIndex: 0,
          // The zoomed range. This is the whole reason the panel exists, so it is never 0..100.
          min: this.passAxisValue?.min ?? 0,
          max: this.passAxisValue?.max ?? 100,
          splitLine: { lineStyle: { color: "#f3f4f6" } },
          axisLabel: { color: "#6b7280", fontSize: 10, formatter: (value) => `${value.toFixed(2)}%` },
        },
        {
          type: "value",
          gridIndex: 1,
          min: 0,
          max: 100,
          splitLine: { lineStyle: { color: "#f3f4f6" } },
          axisLabel: { color: "#6b7280", fontSize: 11, formatter: "{value}%" },
        },
      ],
      series: [this.passSeries(), ...this.columnShadingSeries(), ...this.stackSeriesOptions()],
    })
  }

  // The pass rate, with the reference band behind it and the median drawn through it. The band is
  // what makes a zoomed axis safe: it says which movements are this destination's ordinary jitter,
  // so only the line leaving the band reads as something to look into.
  passSeries() {
    return {
      type: "line",
      name: "Pass rate",
      xAxisIndex: 0,
      yAxisIndex: 0,
      data: this.passPointsValue.map((point) => this.passPoint(point)),
      // No smoothing: a curve would invent values between two days that were never measured.
      smooth: false,
      // A day with no run arrives as null and BREAKS the line here, unlike the stacked chart, which
      // grounds it at zero. On an axis a few points wide a zero would flatten everything else; a
      // gap says "not measured" without claiming a reading.
      connectNulls: false,
      symbol: "circle",
      symbolSize: 5,
      lineStyle: { color: PASS_LINE_COLOR, width: 2 },
      itemStyle: { color: PASS_LINE_COLOR },
      z: 5,
      markArea: this.baselineBand(),
      markLine: this.medianLine(),
    }
  }

  // A day beyond the baseline's ordinary spread gets its verdict's colour at double size, so the
  // days worth investigating are findable without reading the axis. Everything else stays the
  // line's own ink.
  passPoint(point) {
    if (point.value == null) return null

    const color = LEVEL_COLORS[point.level]
    if (!color) return point.value

    return {
      value: point.value,
      symbolSize: 9,
      itemStyle: { color, borderColor: "#ffffff", borderWidth: 1 },
    }
  }

  // The "this is normal" region, median plus and minus one spread. Absent until there are enough
  // days that ran to trust it — an invented band is worse than none, because it licenses a reader
  // to treat noise as settled.
  //
  // markArea hangs off a series and one series can only carry one, so the horizontal band and the
  // vertical column shading are merged here. They are different shapes of the same mechanism: a
  // pair of yAxis bounds spans the panel's width, a pair of xAxis bounds spans its height.
  baselineBand() {
    const bands = []
    const baseline = this.baselineValue

    if (baseline && baseline.low != null) {
      bands.push({
        itemStyle: { color: BAND_COLOR },
        // Declared per-entry rather than on the markArea, because the column shading below wants a
        // different fill and they now share one container.
        data: [{ yAxis: baseline.low }, { yAxis: baseline.high }],
      })
    }

    return this.columnBands(bands)
  }

  // Weekend and no-run columns. Returned as a markArea, or undefined when there is nothing to shade.
  //
  // The y bound is OMITTED rather than set to "min"/"max". Those resolve against the host series'
  // own data extent, not the axis — so on a panel whose axis is deliberately padded past its data
  // the shading would stop short of the edges, and a destination with one day of data (where min
  // equals max) would render every column as a zero-height hairline. A missing coord becomes
  // ±Infinity, which is how a markArea spans the whole panel.
  columnBands(extra = []) {
    const weekend = this.weekendBandsValue.map(([from, to]) => [
      { xAxis: from, itemStyle: { color: this.weekendColorValue } },
      { xAxis: to },
    ])

    // A day with no run breaks the line rather than grounding it at zero, and an unexplained gap
    // reads as a rendering fault. Shading the column says "nothing was measured here" — and it is
    // the WEEKDAY gaps that need it, since a quiet weekend is already shaded as one.
    const noRun = this.noRunCategories().map((label) => [
      { xAxis: label, itemStyle: { color: NO_RUN_COLOR } },
      { xAxis: label },
    ])

    const shaped = extra.map((band) => band.data.map((edge, index) => (index === 0 ? { ...edge, itemStyle: band.itemStyle } : edge)))
    const data = [...shaped, ...weekend, ...noRun]

    return data.length ? { silent: true, data } : undefined
  }

  // The days the pass panel has no reading for. Taken from the pass points rather than recomputed,
  // so the shading cannot disagree with the gap it explains.
  noRunCategories() {
    return this.passPointsValue
      .map((point, index) => (point.value == null ? this.categoriesValue[index] : null))
      .filter((label) => label != null)
  }

  medianLine() {
    const baseline = this.baselineValue
    if (!baseline || baseline.median == null) return undefined

    return {
      silent: true,
      symbol: "none",
      lineStyle: { color: MEDIAN_COLOR, type: "dashed", width: 1 },
      label: { show: false },
      data: [{ yAxis: baseline.median }],
    }
  }

  stackSeriesOptions() {
    return this.stackSeriesValue.map((series) => ({
      type: "bar",
      name: series.name,
      stack: "outcome",
      xAxisIndex: 1,
      yAxisIndex: 1,
      data: series.data,
      barMaxWidth: 28,
      itemStyle: {
        color: series.color,
        borderColor: "#ffffff",
        borderWidth: 1,
      },
      emphasis: { focus: "series" },
    }))
  }

  // A markArea hangs off a series, and hiding that series hides the shading with it: markers render
  // from the legend-FILTERED series list, so hanging the stack's columns on a band that appears in
  // the legend loses every weekend and no-run stripe the moment a reader clicks that band off to
  // inspect the others. This series carries them instead — no data, no legend entry, nothing to
  // toggle.
  columnShadingSeries() {
    const bands = this.columnBands()
    if (!bands) return []

    return [{
      type: "bar",
      name: COLUMN_SHADING_SERIES,
      xAxisIndex: 1,
      yAxisIndex: 1,
      data: [],
      silent: true,
      tooltip: { show: false },
      markArea: bands,
    }]
  }
}
